// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import { ErrorText } from 'components/atoms/ErrorText';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import TableFilterMultiSelect from 'components/molecules/TableFilterMultiSelect';
import TableFilterSelect from 'components/molecules/TableFilterSelect';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { mergeClasses, normalizeNumber } from 'util/commons';
import { DATE_FORMAT } from 'util/ConstantValues';
import { OperatorType, ProcessingMode } from 'util/Enums';
import styles from './PreRegisResourceSelect.module.scss';
import { ResourceTableProps } from '.';
import { useTranslation } from 'react-i18next';
import {
  DetailedHTMLProps,
  ElementType,
  TdHTMLAttributes,
  ThHTMLAttributes,
  useMemo,
} from 'react';
import Popover from 'components/atoms/Popover';
import { IconInfo } from 'components/atoms/icons/IconInfo';

type ResourceCellProps<T extends 'td' | 'th'> = {
  tag: T;
  isAutoFlow?: boolean;
  onAuto?: 'show' | 'hide';
  forceShow?: boolean;
} & (T extends 'td'
  ? DetailedHTMLProps<
      TdHTMLAttributes<HTMLTableCellElement>,
      HTMLTableCellElement
    >
  : DetailedHTMLProps<
      ThHTMLAttributes<HTMLTableCellElement>,
      HTMLTableCellElement
    >);

function ResourceCell<T extends 'td' | 'th'>({
  tag,
  isAutoFlow,
  onAuto = 'hide',
  forceShow,
  ...rest
}: ResourceCellProps<T>) {
  const Tag = tag as ElementType;

  if (
    !forceShow &&
    ((onAuto === 'hide' && isAutoFlow) || (onAuto === 'show' && !isAutoFlow))
  ) {
    return null;
  }

  return <Tag {...rest} />;
}

const ResourceTable: React.FC<ResourceTableProps> = ({
  processingType,
  handleCheckAll,
  filter,
  setFilter,
  resourceData,
  siteOptions,
  materialNameOptions,
  typeOptions,
  operatorType,
  displayResourceData,
  selectedResources,
  isActionDisabled,
  handleCheckItem,
  handleChangeInput,
  isCheckAllDisabled,
  forceShowActionOnAutoFlow,
}) => {
  const { t } = useTranslation();

  const isAutoFlow = useMemo(
    () => processingType === ProcessingMode.Auto,
    [processingType]
  );

  return (
    <table
      className={mergeClasses(
        'primary-table',
        styles.tablePreRegistResourceSelect,
        {
          [styles.autoFlow]: isAutoFlow,
        }
      )}
    >
      <thead>
        <tr>
          <ResourceCell
            tag="th"
            isAutoFlow={isAutoFlow}
            onClick={handleCheckAll}
            className={styles.selection}
            forceShow={forceShowActionOnAutoFlow}
          >
            {!isCheckAllDisabled && (
              <div className={styles.checkAllContainer}>
                <span
                  className={mergeClasses(styles.checkAll, {
                    [styles.disabled]: isCheckAllDisabled,
                  })}
                >
                  {t('pre_regist_resource_select.selection')}
                </span>
              </div>
            )}
          </ResourceCell>
          <th className={styles.dateTimeFilter}>
            <TableFilterCalendar
              label={t('pre_regist_resource_select.date_time')}
              value={filter?.receivedDatetime}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, receivedDatetime: value }))
              }
              disabled={!resourceData.length}
              endDateRequired
            />
          </th>
          <th className={styles.receiveFilter}>
            <TableFilterMultiSelect
              label={t('pre_regist_resource_select.receive_from')}
              options={siteOptions}
              values={filter?.siteIds}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, siteIds: value }))
              }
              disabled={!resourceData.length}
            />
          </th>
          <th className={styles.materialNameFilter}>
            <TableFilterSelect
              label={t('pre_regist_resource_select.material_name')}
              options={materialNameOptions}
              value={filter?.materialName}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, materialName: value }))
              }
              disabled={!resourceData.length}
            />
          </th>
          {operatorType === OperatorType.Recycle && (
            <th className={styles.grade}>
              {t('pre_regist_resource_select.grade')}
            </th>
          )}
          {operatorType === OperatorType.Purchase && (
            <th className={styles.grade}>
              <TableFilterMultiSelect
                label={t('pre_regist_resource_select.type')}
                options={typeOptions}
                values={filter?.types}
                onChange={(value) =>
                  setFilter((prev) => ({ ...prev, types: value }))
                }
                disabled={!resourceData.length}
              />
            </th>
          )}
          <th
            className={
              operatorType === OperatorType.Recycle
                ? styles.resourceIdCol
                : styles.resourceId
            }
          >
            {t('pre_regist_resource_select.resource_id')}
          </th>
          <ResourceCell
            tag="th"
            isAutoFlow={isAutoFlow}
            className={styles.weight}
            onAuto="show"
          >
            {t('pre_regist_resource_select.received_weight')}
          </ResourceCell>
          <th className={styles.weight}>
            {isAutoFlow ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  {t('pre_regist_resource_select.processing_capacity')}
                </span>

                <Popover
                  content={t(
                    'pre_regist_resource_select.processing_capacity_description'
                  )}
                  positions={['top']}
                  containerStyle={{
                    zIndex: '10000',
                  }}
                >
                  <span style={{ display: 'inline-flex' }}>
                    <IconInfo />
                  </span>
                </Popover>
              </div>
            ) : (
              t('pre_regist_resource_select.weight')
            )}
          </th>
          <ResourceCell
            tag="th"
            isAutoFlow={isAutoFlow}
            className={styles.inputWeight}
          >
            {t('pre_regist_resource_select.input_weight')}
          </ResourceCell>
        </tr>
      </thead>

      <tbody>
        {displayResourceData.length > 0 ? (
          displayResourceData.map((item, index) => {
            const isItemDisabled = isActionDisabled(item);

            return (
              <tr
                key={index}
                className={mergeClasses({
                  [styles.disabledRow]: isItemDisabled,
                })}
              >
                <ResourceCell
                  tag="td"
                  isAutoFlow={isAutoFlow}
                  className={styles.selection}
                  forceShow={forceShowActionOnAutoFlow}
                >
                  {!isItemDisabled && (
                    <CustomCheckBox
                      id={item.processingObjectId}
                      checked={Boolean(
                        selectedResources[item.processingObjectId]?.checked
                      )}
                      onChange={(e) => handleCheckItem(e.target.checked, item)}
                      darkMode
                    />
                  )}
                </ResourceCell>
                <td className={styles.dateTimeFilter}>
                  {getFormatDateWithCurrentTimezone(
                    item.receivedDatetime,
                    DATE_FORMAT.slaYMDHm
                  )}
                </td>
                <td className={styles.receiveFilter}>{item.site.name}</td>
                <td className={styles.materialNameFilter}>
                  {item.materialName}
                </td>
                {operatorType === OperatorType.Recycle && (
                  <td className={styles.grade}>{item.grade?.name}</td>
                )}
                {operatorType === OperatorType.Purchase && (
                  <td className={styles.grade}>{item.type}</td>
                )}
                <td
                  className={
                    operatorType === OperatorType.Recycle
                      ? styles.resourceIdCol
                      : styles.resourceId
                  }
                >
                  {item.processingObjectId}
                </td>
                <ResourceCell
                  tag="td"
                  isAutoFlow={isAutoFlow}
                  className={styles.weight}
                  onAuto="show"
                >
                  {normalizeNumber({
                    value: item.weight,
                  })}
                  &nbsp;kg
                </ResourceCell>
                <td className={styles.weight}>
                  {normalizeNumber({
                    value: item.remainingWeight,
                  })}
                  &nbsp;kg
                </td>
                <ResourceCell
                  tag="td"
                  isAutoFlow={isAutoFlow}
                  className={styles.inputWeight}
                >
                  <div
                    className={mergeClasses(
                      styles.inputWeightCol,
                      styles.inputWeightColInput
                    )}
                  >
                    <input
                      className={mergeClasses(styles.input, {
                        [styles.inputError]:
                          selectedResources[item.processingObjectId]?.hasError,
                        [styles.disabled]: isItemDisabled,
                      })}
                      disabled={isItemDisabled}
                      type="text"
                      onChange={(e) => handleChangeInput(e.target.value, item)}
                      value={
                        selectedResources[item.processingObjectId]?.weight || ''
                      }
                    />

                    <span className={styles.unit}>{'kg'}</span>
                  </div>

                  {selectedResources[item.processingObjectId]?.hasError && (
                    <ErrorText
                      text={
                        t('pre_regist_resource_select.message_error', {
                          field: item.remainingWeight,
                        }) || ''
                      }
                      className={styles.error}
                    />
                  )}
                </ResourceCell>
              </tr>
            );
          })
        ) : (
          <tr>
            <td
              colSpan={operatorType === OperatorType.Purchase ? 11 : 10}
              className={styles.msgError}
            >
              {t('messages.M_008')}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default ResourceTable;
